import { useState, useEffect, useMemo, useCallback, type JSX } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useLocation, useNavigate } from 'react-router';
import useBreakpoints from './useBreakpoints';
import useLocalization from './useLocalization';
import { useSearchContext } from '../contexts/SearchContext';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import useProductAvailability from './useProductAvailability';
import { FEATURE_FLAG } from '../constants/featureFlags';

export type TabItem = {
  label: string;
  value: string;
  icon?: JSX.Element;
};

export type TabsSection = {
  common: TabItem[];
  auth: TabItem[];
  public: TabItem[];
};

export type TabsConfig = {
  mobile: TabsSection;
  desktop: TabsSection;
};

const CASINO_TAB_VALUE = '/casino';
const PROMOTIONS_TAB: TabItem = { label: 'promotions', value: '/promotions' };

const getTabs = (config: TabsConfig, isMobile: boolean, isAuthenticated: boolean): TabItem[] => {
  const { common, auth, public: pub } = isMobile ? config.mobile : config.desktop;
  return [...common, ...(isAuthenticated ? auth : pub)];
};

const getCasinoProductTab = (config: TabsConfig, isMobile: boolean): TabItem | undefined => {
  const { common, auth, public: pub } = isMobile ? config.mobile : config.desktop;

  return [...common, ...auth, ...pub].find((tab) => tab.value === CASINO_TAB_VALUE);
};

const getCasinoOnlyTabs = (config: TabsConfig, isMobile: boolean, promotionsEnabled: boolean): TabItem[] => {
  const casinoProductTab = getCasinoProductTab(config, isMobile);

  if (!casinoProductTab) {
    return [];
  }

  return [casinoProductTab, ...(promotionsEnabled ? [PROMOTIONS_TAB] : [])];
};

export const useHeaderTabs = (config: TabsConfig) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useBreakpoints();
  const { isAuthenticated } = useAuth();
  const { hasSportsbookAccess, hasCasinoAccess } = useProductAvailability();
  const promotionsEnabled = useFeatureIsOn(FEATURE_FLAG.PROMOTIONS_ENABLED);
  const { getLocalizedConfig } = useLocalization();
  const { showSearchBar } = useSearchContext();
  const isOnSearchPage = location.pathname === '/search';

  const [selectedTab, setSelectedTab] = useState<number | false>(0);

  const tabs = useMemo(() => {
    const baseTabs = getTabs(config, isMobile, isAuthenticated);

    if (!hasSportsbookAccess) {
      if (!hasCasinoAccess) {
        return [];
      }

      return getCasinoOnlyTabs(config, isMobile, promotionsEnabled);
    }

    if (!hasCasinoAccess) {
      return baseTabs.filter((tab) => tab.value !== CASINO_TAB_VALUE);
    }

    return baseTabs;
  }, [config, hasCasinoAccess, hasSportsbookAccess, isAuthenticated, isMobile, promotionsEnabled]);

  const localizedTabs = useMemo(() => getLocalizedConfig(tabs), [tabs, getLocalizedConfig]);

  useEffect(() => {
    const currentTab = tabs.findIndex((tab) => location.pathname.startsWith(tab.value));
    setSelectedTab(currentTab === -1 ? false : currentTab);
  }, [location.pathname, tabs]);

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      const tabValue = tabs[newValue].value;
      setSelectedTab(newValue);
      navigate(tabValue);
    },
    [navigate, tabs]
  );

  return {
    showSearchBar: showSearchBar || isOnSearchPage,
    selectedTab,
    localizedTabs,
    handleChange,
  };
};
